import { useQuery } from '@tanstack/react-query';
import { NoiseEventsHistoryQueryVariables } from 'app/__generated__/global';
import { KnownAccessControls } from 'app/business-logic/security/KnownAccessControls';
import { QUERY_KEY } from 'app/business-logic/services/enums';
import {
  getNoiseEventsHistoryBase,
  getNoiseEventsHistoryClassification,
  getNoiseEventsHistoryResourceGroups,
} from 'app/business-logic/services/noise-service/getNoiseEventsHistory';
import { useHasAccess } from 'app/hooks/use-has-access';
import { useMemo } from 'react';

export function useEventHistoryDataQuery(requestParams: NoiseEventsHistoryQueryVariables) {
  const hasAccess = useHasAccess();
  const hasNoiseEventsAccess = hasAccess(KnownAccessControls.app.events.noise._);
  const hasResourceGroupsAccess = hasAccess(KnownAccessControls.app.config.resourceGroups._);
  const hasClassificationAccess = hasAccess(KnownAccessControls.app.noise.classification.data.read._);

  const { from, to, noiseEventIds } = requestParams.filter;
  const isEnabled = hasNoiseEventsAccess && ((!!from && !!to) || (!!noiseEventIds && noiseEventIds.length > 0));
  const {
    data: baseData,
    isFetching: baseIsFetching,
    isLoading: baseIsLoading,
    error: baseError,
  } = useQuery({
    queryFn: () => getNoiseEventsHistoryBase(requestParams),
    queryKey: [QUERY_KEY.noiseEventHistoryBase, requestParams],
    placeholderData: previousData => previousData,
    enabled: isEnabled,
    retry: false,
  });

  const {
    data: resourceGroupsData,
    isFetching: resourceGroupsIsFetching,
    error: resourceGroupsError,
  } = useQuery({
    queryFn: () => getNoiseEventsHistoryResourceGroups(requestParams),
    queryKey: [QUERY_KEY.noiseEventHistoryResourceGroups, requestParams],
    placeholderData: previousData => previousData,
    enabled: isEnabled && hasResourceGroupsAccess,
    retry: false,
  });

  const {
    data: classificationData,
    isFetching: classificationIsFetching,
    error: classificationError,
  } = useQuery({
    queryFn: () => getNoiseEventsHistoryClassification(requestParams),
    queryKey: [QUERY_KEY.noiseEventHistoryClassification, requestParams],
    placeholderData: previousData => previousData,
    enabled: isEnabled && hasClassificationAccess,
    retry: false,
  });

  const combinedData = useMemo(() => {
    if (!baseData?.results) return;
    return {
      ...baseData,
      results: baseData.results.flatMap(result => {
        if (!result) return [];
        const resourceGroupsInfo = resourceGroupsData?.results.find(e => e?.id === result.id);
        const classificationInfo = classificationData?.results.find(e => e?.id === result.id);
        return {
          ...result,
          noiseEventPolicyOverride: resourceGroupsInfo?.noiseEventPolicyOverride ?? [],
          classifications: classificationInfo?.classifications ?? [],
          comments: classificationInfo?.comments ?? null,
          disregarded: classificationInfo?.disregarded ?? false,
          noiseEventId: result.id,
        };
      }),
    };
  }, [baseData, resourceGroupsData?.results, classificationData?.results]);

  const error = baseError || resourceGroupsError || classificationError;

  return {
    data: combinedData,
    isFetching: baseIsFetching,
    isLoading: baseIsLoading,
    isResourceGroupsFetching: hasResourceGroupsAccess && resourceGroupsIsFetching,
    isClassificationFetching: hasClassificationAccess && classificationIsFetching,
    error,
  };
}
